import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = "https://nissi-india.com";

const EmployeeOpeningLeavePrint = () => {
  const finYearId = localStorage.getItem("finYearId") || "";

  const qs = useMemo(() => new URLSearchParams(window.location.search), []);
  const empId = qs.get("code") || "";
  const year = qs.get("year") || "";

  const [rows, setRows] = useState([]);
  const [totalLapsed, setTotalLapsed] = useState(0);
  const [loading, setLoading] = useState(false);

  // ✅ for fixing white print page
  const [logoReady, setLogoReady] = useState(false);
  const [autoPrinted, setAutoPrinted] = useState(false);

  const fetchPrint = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(empId).trim() || !String(year).trim()) {
      toast.error("Missing code/year in URL");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-opening-leave/print-data`, {
        params: {
          finYearId: Number(finYearId),
          code: Number(empId),
          year: Number(year),
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotalLapsed(res.data.totalLapsed || 0);
      } else {
        toast.error(res?.data?.message || "Failed to load print");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ print after DOM paint
  const handlePrint = () => {
    requestAnimationFrame(() => {
      setTimeout(() => window.print(), 50);
    });
  };

  // ✅ auto-print ONLY after data + logo loaded (prevents white page)
  useEffect(() => {
    if (autoPrinted) return;
    if (loading) return;
    if (!rows || rows.length === 0) return;
    if (!logoReady) return;

    setAutoPrinted(true);
    handlePrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rows, logoReady, autoPrinted]);

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={2000} />

      <style>{`
      
        .page { max-width: 1200px; margin: 0 auto; padding: 14px; }
        .box { border: 2px solid #000; padding: 10px; }
        .header { display: flex; gap: 12px; align-items: center; }
        .logo { width: 110px; height: 95px; border: 1px solid #ddd; display:flex; align-items:center; justify-content:center; overflow:hidden; }
        .logo img { width: 100%; height: 100%; object-fit: contain; }
        .center { flex: 1; text-align: center; line-height: 1.2; }
        .right { width: 220px; text-align:right; font-size: 12px; line-height: 1.3; }
        .title { text-align:center; margin: 12px 0 8px; font-weight: 900; font-size: 18px; }
        .wrap { overflow: auto; }

        table { width: 100%; border-collapse: collapse; border: 1px solid #000; }
        th, td { border: 1px solid #000; padding: 6px; font-size: 12px; text-align: center; vertical-align: middle; }
        td.left { text-align: left; }

        .printed { margin-top: 10px; text-align:right; font-size:12px; }

        .printBtnWrap { text-align:right; margin-bottom: 10px; }
        .printBtn {
          padding: 6px 14px;
          font-weight: 700;
          border: 1px solid #000;
          background: #fff;
          cursor: pointer;
        }

        @media print {
         html, body {
    height: auto !important;
    overflow: visible !important;
  }

  body {
    display: block !important;
  }

          .page { max-width: none !important; margin: 0 !important; padding: 0 !important; }
          .wrap { overflow: visible !important; }

          /* ✅ hide print button in print */
          .printBtnWrap { display: none !important; }

          table { table-layout: fixed !important; width: 100% !important; }
          th, td {
            font-size: 10px !important;
            padding: 4px !important;
            white-space: normal !important;
            word-break: break-word !important;
            overflow-wrap: break-word !important;
          }

          thead { display: table-header-group; }
          tr { page-break-inside: avoid !important; }
          @page { size: A4 landscape; margin: 10mm; }
        }
      `}</style>

      <div className="page">
        <div className="box">
          <div className="header">
            <div className="logo">
              <img
                src="/assets/nissi-logo.png"
                alt="Nissi Logo"
                onLoad={() => setLogoReady(true)}
                onError={() => setLogoReady(true)} // allow print even if image missing
              />
            </div>

            <div className="center">
              <div style={{ fontSize: 20, fontWeight: 900 }}>Nissi Infotech</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Nissi</div>
              <div style={{ fontSize: 14, fontWeight: 900, marginTop: 4 }}>CIVIL CONSTRUCTION</div>
              <div style={{ fontSize: 12, marginTop: 8 }}>
                3/42-12, Nagappa colony, kavundampalayam, Tamil Nadu, India.
              </div>
              <div style={{ fontSize: 12 }}>
                Phone No : 9003744644 &nbsp; | &nbsp; infrasoft-implementation@nissiinfotech.in
              </div>
            </div>

            <div className="right">
              <div style={{ fontWeight: 800 }}>Employee Opening Leave</div>
              <div>
                Year: <b>{year}</b>
              </div>
              <div>
                Emp ID: <b>{empId}</b>
              </div>
            </div>
          </div>

          {/* ✅ Print button */}
          <div className="printBtnWrap">
            <button onClick={handlePrint} className="printBtn" disabled={loading || rows.length === 0}>
              Print
            </button>
          </div>

          <div className="title">Employee Opening Leave ({year})</div>

          <div className="wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 45 }}>S.No</th>
                  <th style={{ width: 90 }}>Emp Code</th>
                  <th style={{ width: 140 }}>Emp Name</th>
                  <th style={{ width: 60 }}>Year</th>
                  <th style={{ width: 80 }}>Month</th>
                  <th style={{ width: 80 }}>TotalDays</th>
                  <th style={{ width: 90 }}>Workingdays</th>
                  <th style={{ width: 80 }}>TotalLeave</th>
                  <th style={{ width: 120 }}>MonthlyCasualleave</th>
                  <th style={{ width: 90 }}>Balanceleave</th>
                  <th style={{ width: 110 }}>OpenningLeave</th>
                  <th style={{ width: 90 }}>SavingLeave</th>
                  <th style={{ width: 110 }}>BalanceLeave/LOP</th>
                  <th style={{ width: 90 }}>Lapsed Leave</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={14}>Loading...</td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={14}>No Records Found</td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{r.fld_Employeecode}</td>
                      <td className="left">{r.fld_Employee}</td>
                      <td>{r.year}</td>
                      <td>{r.fld_branchname}</td> {/* Month */}
                      <td>{r.April}</td> {/* TotalDays */}
                      <td>{r.attDaysCount}</td> {/* Workingdays */}
                      <td>{r.May}</td> {/* TotalLeave */}
                      <td>{r.June}</td> {/* MonthlyCasualleave */}
                      <td>{r.TotBalanceLeaveTemp}</td> {/* Balanceleave */}
                      <td>{r.September}</td> {/* OpenningLeave */}
                      <td>{r.October}</td> {/* SavingLeave */}
                      <td>{r.November}</td> {/* BalanceLeave/LOP */}
                      <td>
                        <b>{r.December}</b> {/* Lapsed Leave */}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan={13} style={{ fontWeight: 900 }}>
                    Total
                  </td>
                  <td style={{ fontWeight: 900 }}>{totalLapsed}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="printed">
            Printed on: <b>{new Date().toLocaleString()}</b>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeOpeningLeavePrint;
